import React, { useState, useRef, useEffect } from "react";
import logo from "../assets/images/logo.png";
import cancel from "../assets/images/navbar/cancel.png";
import arrow from "../assets/images/order/orange-arrow.png";

type OtpModalProps = {
  isOpen: boolean;
  onClose: () => void;
  mobileNumber: string;
  onVerify?: (otp: string) => void;
  autoFillOtp?: string;
};

const Otp: React.FC<OtpModalProps> = ({ isOpen, onClose, mobileNumber, onVerify, autoFillOtp }) => {
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [showOtpPopup, setShowOtpPopup] = useState(false);

  // Show OTP popup when autoFillOtp is provided
  useEffect(() => {
    if (autoFillOtp && autoFillOtp.length === 6) {
      setShowOtpPopup(true);
    }
  }, [autoFillOtp]);

  if (!isOpen) return null;

  const handleOtpChange = (index: number, value: string) => {
    if (!/^[0-9]*$/.test(value)) return; // Only allow numbers

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Take only the last character
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = () => {
    const otpValue = otp.join("");
    if (otpValue.length === 6) {
      if (onVerify) {
        onVerify(otpValue);
      }
    } else {
      alert("Please enter the complete 6-digit OTP");
    }
  };

  return (
    <>
      {/* OTP Display Popup */}
      {showOtpPopup && autoFillOtp && (
        <div className="fixed inset-0 z-[1005] flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-60" />
          <div className="relative bg-white rounded-[24px] p-8 shadow-2xl max-w-[400px] w-full mx-4">
            <h3 className="text-[#050505] font-Urbanist text-[24px] font-semibold leading-[125%] mb-4 text-center">
              Your OTP
            </h3>
            {/* <p className="text-[#6A6A6A] font-Urbanist text-[16px] font-medium leading-[140%] mb-6 text-center">
              Please note down the OTP and enter it in the fields below
            </p> */}
            <div className="bg-[#FFF5E6] rounded-[12px] p-6 mb-6">
              <p className="text-[#FF8A00] font-Urbanist text-[36px] font-bold leading-[125%] text-center tracking-[8px]">
                {autoFillOtp}
              </p>
            </div>
            <button
              onClick={() => setShowOtpPopup(false)}
              className="w-full py-4 px-6 bg-[#FF8A00] rounded-[150px] cursor-pointer hover:bg-[#E67A00] transition-colors"
            >
              <span className="text-white font-Urbanist text-[18px] font-semibold leading-[140%]">
                OK
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Backdrop Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[1003]"
      />

      {/* Modal Container */}
      <div className="fixed inset-0 flex items-center justify-center z-[1004] pointer-events-none">
        <div
          className="w-full h-full md:w-[545px] md:h-auto p-6 md:p-8 rounded-none md:rounded-[24px] bg-white pointer-events-auto relative"
          style={{
            boxShadow: "-23px 23px 72px 0 rgba(0, 0, 0, 0.15)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <div
            className="absolute top-4 right-4 md:top-6 md:right-6 h-[36px] w-[36px] cursor-pointer border rounded-full p-[7px] bg-white flex items-center justify-center"
            onClick={onClose}
          >
            <img src={cancel} alt="Close" className="h-[18px] w-[18px]" />
          </div>

          {/* Logo */}
          <div className="mb-6 md:mb-[36px] mt-4 md:mt-0">
            <img src={logo} alt="Duddus" className="h-[54px] w-[166px]" />
          </div>

          {/* Title */}
          <h2 className="text-[#050505] font-Urbanist text-2xl md:text-[32px] font-semibold leading-[125%] mb-2 md:mb-[8px]">
            Enter OTP
          </h2>

          {/* Subtitle */}
          <p className="text-[#6A6A6A] font-Urbanist text-sm md:text-[16px] font-medium leading-[140%] mb-6 md:mb-[24px]">
            Enter 6 digit OTP sent to {mobileNumber}
          </p>

          {/* OTP Input Boxes */}
          <div className="flex justify-center gap-2 md:gap-3 mb-6 md:mb-[27px]">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-[45px] h-[45px] md:w-[60px] md:h-[60px] text-center text-[20px] md:text-[24px] font-semibold border-b-2 border-[#E0E0E0] rounded-[12px] outline-none focus:border-[#FF8A00] transition-colors shadow-[0_1px_4px_0_rgba(229,229,229,0.40)] bg-[#FAFAFA]"
              />
            ))}
          </div>
          <div className="text-center mb-8 md:mb-[100px]">
            <p className="text-[#6A6A6A] font-Urbanist text-sm md:text-[16px] font-medium leading-[140%] mb-6 md:mb-[24px]">
              Didn't received OTP? Resend in{" "}
              <span className="text-[#7604A5]">00:27</span>
            </p>
          </div>

          {/* Verify Button */}
          <button
            onClick={handleVerify}
            className="w-full flex items-center justify-between py-4 px-6 bg-[#FF8A00] rounded-[150px] cursor-pointer hover:bg-[#E67A00] transition-colors"
          >
            <span className="text-white font-Urbanist text-[20px] font-semibold leading-[140%]">
              Verify OTP
            </span>
            <div className="h-[42px] w-[42px] flex justify-center items-center bg-white rounded-full">
              <img
                src={arrow}
                alt="right arrow"
                className="h-[41px] w-[30px] stroke-[2.587px] stroke-[#FB8606]"
              />
            </div>
          </button>
        </div>
      </div>
    </>
  );
};

export default Otp;
        
